/**
 * src/modules/orders-list/table.ts — Faza 6.2.24 (2026-05-03) split.
 *
 * Legacy <table> renderer (gdy `?virtual=0` lub virtual mode niedostępny).
 * Pełne filtering (status, source, payment, shipment, search), sortowanie,
 * paginacja (`state.itemsPerPage`), bind eye/edit/delete/checkbox events.
 *
 * Wycięte z `src/modules/orders-list.ts` (1651 LOC) — patrz
 * `.plans/audit-orders-list-split.md`.
 */

import type { OrderItem, OrderStatusDef } from '../../types/dto.ts';

import { $ordersHydrated } from '../../stores/orders.js';
import { statusFilterMatches, isOrdersGlobalSearchActive, orderSearchGroupContextHtml } from '../orders-shared.ts';

var state: any = (typeof window !== 'undefined' && window.__sbState) || (function () {
  var _p = new Proxy({}, {
    get: function (_, k: any) { return window.__sbState ? (window.__sbState as any)[k] : undefined; },
    set: function (_, k: any, v: any) { if (window.__sbState) (window.__sbState as any)[k] = v; return true; },
    has: function (_, k: any) { return window.__sbState ? (k in (window.__sbState as any)) : false; },
  });
  return _p;
})();

// Lazy delegates (cross-module)
function _isOrdersVirtualMode(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports._isOrdersVirtualMode) || (window as any)._isOrdersVirtualMode) as any).apply(this, args); }
function _renderOrdersVirtualList(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports._renderOrdersVirtualList) || (window as any)._renderOrdersVirtualList) as any).apply(this, args); }
function _lazyRefreshVisiblePSOrders(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports._lazyRefreshVisiblePSOrders) || (window as any)._lazyRefreshVisiblePSOrders) as any).apply(this, args); }
function _updateBulkBar(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports._updateBulkBar) || (window as any)._updateBulkBar) as any).apply(this, args); }
function showOrderDetail(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports.showOrderDetail) || window.showOrderDetail) as any).apply(this, args); }
function renderPagination(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports.renderPagination) || window.renderPagination) as any).apply(this, args); }

// 2026-06-10 BUGFIX: order.status bywa STRINGIEM (rekord, ktory nie przeszedl przez
// normalizeBackendOrder). Bez guardu odczyt .key/.label/.color rzucal/dawal undefined →
// pusty kafelek statusu, a filtr statusu nie matchowal stringa. Mirror guardu z cards.ts.
function coerceStatusObj(status: any): { key: string; label: string; color: string } {
  if (status && typeof status === 'object') {
    return { key: status.key || '', label: status.label || '', color: status.color || 'info' };
  }
  if (status) {
    var w: any = window;
    var clrFn = (w.__sbExports && w.__sbExports.statusColorForLabel) || w.statusColorForLabel;
    var color = 'info';
    if (typeof clrFn === 'function') { try { color = clrFn(String(status)); } catch (_) {} }
    return { key: String(status).toLowerCase().replace(/\s+/g, '_'), label: String(status), color: color };
  }
  return { key: 'new', label: 'Nowe', color: 'info' };
}

export function renderOrdersTable() {
    var listContainer = document.getElementById('orders-list-container');
    if (!listContainer) return;

    // Faza L.4 — virtual mode dispatch BEFORE table inject.
    // Inline filtry zgodne z pełnym filtering w table mode (linia ~243+).
    if (_isOrdersVirtualMode() && $ordersHydrated.get() && SellBaseData.orders && SellBaseData.orders.length) {
      var filteredV = SellBaseData.orders;
      var filterStatusV = (state as any).ordersStatusFilter || 'all';
      var filterSourceV = (state as any).ordersSourceFilter || 'all';
      if (!isOrdersGlobalSearchActive() &&
          (!Array.isArray((state as any).ordersFilterStatuses) || !(state as any).ordersFilterStatuses.length) && filterStatusV !== 'all') {
        filteredV = filteredV.filter(function(o: any) {
          var so = coerceStatusObj(o && o.status);
          // #7 (2026-08-13): KLUCZ, nie nazwa (nazwa jest zmienialna — ta sama
          // mina co ETAP 3) + matcher wielokluczowy (klik w grupę = zlepek).
          return statusFilterMatches(so.key || so.label || '', filterStatusV);
        });
      }
      if ((!Array.isArray((state as any).ordersFilterSources) || !(state as any).ordersFilterSources.length) && filterSourceV !== 'all') {
        filteredV = filteredV.filter(function(o: any) { return o.source && o.source.key === filterSourceV; });
      }
      _renderOrdersVirtualList(filteredV);
      return;
    }

    // 2026-05-25: nowy układ tabeli — czystszy, tylko niezbędne kolumny.
    // Wywalone: ID (w drawerze), Klient (info widoczne w drawerze), Akcje (klik wiersza = drawer).
    // Checkboxy: hide by default, show po "Wybierz zamówienia" (state.ordersSelectMode), mirror cards.ts.
    var selectMode = !!(state as any).ordersSelectMode;
    var checkColStyle = selectMode ? 'width:32px' : 'width:0;padding:0;display:none';
    if (!document.getElementById('orders-table-body')) {
      listContainer.innerHTML =
        '<div class="orders-table-wrap">' +
          '<table class="data-table orders-table-v2">' +
            '<thead><tr>' +
              '<th class="orders-table-check-col" style="' + checkColStyle + '"><input type="checkbox" id="orders-table-check-all" aria-label="Zaznacz wszystkie"></th>' +
              '<th>Produkty</th>' +
              '<th>Status</th>' +
              '<th>Źródło</th>' +
              '<th data-sort="amount" style="cursor:pointer;white-space:nowrap">Kwota <span style="opacity:.5">↕</span></th>' +
              '<th data-sort="date" style="cursor:pointer;white-space:nowrap">Data <span style="opacity:.5">↕</span></th>' +
            '</tr></thead>' +
            '<tbody id="orders-table-body"></tbody>' +
          '</table>' +
        '</div>';
    } else {
      // Update check column visibility gdy select mode się zmienił
      var existingCheckTh = listContainer.querySelector('.orders-table-check-col') as HTMLElement | null;
      if (existingCheckTh) existingCheckTh.setAttribute('style', checkColStyle);
    }

    var container = document.getElementById('orders-table-body');
    if (!container) return;

    // FIX (2026-04-29): skeleton loader gdy dane jeszcze nie pobrane.
    // Bez tego user widział "Brak zamówień spełniających kryteria" przez 200-500ms
    // zanim loadAllegroOrdersIntoPanel() (fire-and-forget) zwrócił pierwszą paczkę.
    // FIX (Faza E.2 POC): czyta z Nanostore zamiast state.X. Bridge w _legacy-bridge.js
    // synchronizuje $ordersHydrated ↔ window.__sbState.ordersHydrated, więc legacy code
    // (allegro-tab.js linia 1088 `state.ordersHydrated = true`) nadal działa transparentnie.
    if (!$ordersHydrated.get() && (!SellBaseData.orders || SellBaseData.orders.length === 0)) {
      // Skeleton 6 cols (checkbox + 5 cols), checkbox col styled by header (display:none gdy off)
      var skeletonRow =
        '<tr class="orders-skeleton-row" aria-busy="true">' +
          '<td class="orders-table-check-col" style="' + checkColStyle + '"><div style="width:14px;height:14px;background:var(--color-border);border-radius:3px;animation:sb-pulse 1.4s ease-in-out infinite"></div></td>' +
          '<td><div style="width:75%;height:12px;background:var(--color-border);border-radius:3px;animation:sb-pulse 1.4s ease-in-out infinite"></div></td>' +
          '<td><div style="width:50%;height:18px;background:var(--color-border);border-radius:9px;animation:sb-pulse 1.4s ease-in-out infinite"></div></td>' +
          '<td><div style="width:40%;height:12px;background:var(--color-border);border-radius:3px;animation:sb-pulse 1.4s ease-in-out infinite"></div></td>' +
          '<td><div style="width:50%;height:12px;background:var(--color-border);border-radius:3px;animation:sb-pulse 1.4s ease-in-out infinite"></div></td>' +
          '<td><div style="width:60%;height:12px;background:var(--color-border);border-radius:3px;animation:sb-pulse 1.4s ease-in-out infinite"></div></td>' +
        '</tr>';
      container.innerHTML = skeletonRow.repeat(6);
      // Wstrzyknij keyframes raz (idempotent)
      if (!document.getElementById('sb-skeleton-keyframes')) {
        var st = document.createElement('style');
        st.id = 'sb-skeleton-keyframes';
        st.textContent = '@keyframes sb-pulse{0%,100%{opacity:0.45}50%{opacity:0.85}}';
        document.head.appendChild(st);
      }
      // FIX (Faza E.2): reaktywna subskrypcja — gdy $ordersHydrated.set(true) zostanie
      // wywołane (z allegro-tab.js po fetch), automatycznie re-render bez ręcznego polling.
      // To pokazuje real value Nanostores: zamiast manualnego renderOrdersView() w callback,
      // każdy moduł który zmieni stan triggeruje wszystkie zainteresowane render-funkcje.
      if (!((container as any)._sbHydratedUnsub)) {
        (container as any)._sbHydratedUnsub = $ordersHydrated.subscribe(function(value) {
          if (value && container!.querySelector('.orders-skeleton-row')) {
            // Unsubscribe + trigger normal render (skeleton znika, prawdziwe rows renderują się).
            try { (container! as any)._sbHydratedUnsub(); } catch (_: any) {}
            (container! as any)._sbHydratedUnsub = null;
            renderOrdersTable();
          }
        });
      }
      return;
    }

    var filtered = SellBaseData.orders;

    // Use state filters
    var filterStatus = (state as any).ordersStatusFilter || 'all';
    var filterSource = (state as any).ordersSourceFilter || 'all';
    var searchEl = document.getElementById('order-search');
    var searchQuery = searchEl ? (searchEl as any).value.trim() : '';
// @ts-ignore -- TS6133 unused
    var filterAmountMin = NaN;
// @ts-ignore -- TS6133 unused
    var filterAmountMax = NaN;

    if (!isOrdersGlobalSearchActive() &&
        (!Array.isArray((state as any).ordersFilterStatuses) || !(state as any).ordersFilterStatuses.length) &&
        filterStatus && filterStatus !== 'all') {
      filtered = filtered.filter(function(o: any) {
        var so = coerceStatusObj(o && o.status);
        // ETAP 3: filtr trzyma KLUCZ statusu, nie jego zmienialną nazwę.
        // #7 (2026-08-13): matcher wielokluczowy — klik w grupę wysyła zlepek.
        return statusFilterMatches(so.key || '', filterStatus);
      });
    }

    if ((!Array.isArray((state as any).ordersFilterSources) || !(state as any).ordersFilterSources.length) &&
        filterSource && filterSource !== 'all') {
      filtered = filtered.filter(function(o: any) { return o.source.key === filterSource; });
    }

    // 2026-06-09: status płatności idzie SERVER-SIDE (param payment_status w auth-sync.ts).
    // Client-side zostaje TYLKO 'cod' (metoda płatności, nie status SellBase).
    // 2026-09-03 (audyt): 'cod' idzie server-side (payment_dims=cod,cod_paid w auth-sync.ts).
    // Shipment filter — 2026-06-09: SERVER-SIDE (param shipment_status). Brak filtra client-side.

    if (searchQuery && String((state as any).ordersFilterQuery || '').trim().length < 2 && !(state as any).ordersSearchActive) {
      // deburrText (0180): 'jedrzej' matchuje 'Jędrzej' także w lokalnym filtrze
      var _deburr = (window as any).deburrText || function(s: any) { return String(s || '').toLowerCase(); };
      var q = _deburr(searchQuery);
      filtered = filtered.filter(function(o: any) {
        var productsText = '';
        if (Array.isArray(o.products)) {
          productsText = o.products.join(' ');
        } else if (Array.isArray(o.items)) {
          productsText = o.items.map(function(it: OrderItem) { return it && it.name ? it.name : ''; }).join(' ');
        }
        return _deburr(o.id).indexOf(q) !== -1 ||
               _deburr(o.customer).indexOf(q) !== -1 ||
               _deburr(productsText).indexOf(q) !== -1;
      });
    }

    // Sorting
    if ((state as any).sortColumn) {
      filtered = filtered.slice().sort(function(a, b) {
        var valA, valB;
        switch ((state as any).sortColumn) {
          case 'id': valA = a.id; valB = b.id; break;
          case 'customer': valA = a.customer; valB = b.customer; break;
          case 'amount': valA = a.amount; valB = b.amount; break;
          case 'date': valA = a.date; valB = b.date; break;
          default: return 0;
        }
        if (valA < valB) return (state as any).sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return (state as any).sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    // Sprint 25 (2026-05-03): server-side pagination.
    // SellBaseData.orders zawiera już tylko bieżącą stronę (max 50 row).
    // `filtered` zostaje aplikowane jako optional klient-side filter na widoczne row,
    // ale paginacja używa `state.ordersTotalCount`/`ordersTotalPages` z server response.
    // Klik strony w pagination → renderOrdersView() → loadAllegroOrdersIntoPanel(true) → server fetch.
    var page = (state as any).currentPage.orders || 1;
    var totalItems = (state as any).ordersTotalCount != null ? (state as any).ordersTotalCount : filtered.length;
    var totalPages = (state as any).ordersTotalPages || Math.ceil(totalItems / (state as any).itemsPerPage) || 1;
    if (page > totalPages && totalPages > 0) page = totalPages;
    var paginated = filtered;  // już z server, bez slice

    // 2026-07-29: w tabeli nie ma miejsca na treść — pomarańczowe ikonki z tooltipem,
    // żeby wiersz nie „gubił" informacji, którą karta pokazuje wprost.
    function _noteFlagsHtml(order: any): string {
      var out = '';
      var msg = String((order && (order.message_to_seller || order.buyer_message)) || '').trim();
      var note = (order && order.internal_note) || null;
      var noteTxt = note ? String(note.text || '').trim() : '';
      if (msg) {
        out += '<span class="ord-note-flag" title="Wiadomość od klienta: ' + escapeAttr(msg) + '">'
          + '<i data-lucide="message-square"></i></span>';
      }
      if (noteTxt) {
        var au = String((note && note.author) || '').trim();
        out += '<span class="ord-note-flag" title="Notatka' + (au ? ' (' + escapeAttr(au) + ')' : '') + ': ' + escapeAttr(noteTxt) + '">'
          + '<i data-lucide="sticky-note"></i></span>';
      }
      return out;
    }

    // 2026-05-25: helpers wzorowane na dashboardzie (legacy-dashboard-orchestrator.ts renderRecentOrders).
    // Produkty: nazwa 1. + "+N więcej" pill. Źródło: "Allegro · Konto". Data: dd.mm.yyyy HH:mm.
    function _fmtItems(order: any): string {
      var items = Array.isArray(order.items) ? order.items : [];
      if (!items.length) {
        var n = parseInt(order.productCount || 0, 10);
        return n > 0 ? '<span style="font-size:11px">' + n + ' szt.</span>' :
          '<span style="color:var(--color-text-muted);font-size:11px">—</span>';
      }
      var first = items[0];
      var name = String((first && (first.name || first.product_name || first.title)) || '—');
      var truncated = name.length > 48 ? name.substring(0, 45) + '…' : name;
      var h = '<span title="' + escapeAttr(name) + '" style="font-size:11px">' + escapeHtml(truncated) + '</span>';
      var extra = items.length - 1;
      if (extra > 0) {
        h += ' <span style="font-size:10px;color:var(--color-text-muted);background:var(--color-surface-2);padding:1px 5px;border-radius:8px;margin-left:4px">+' + extra + ' więcej</span>';
      }
      return h;
    }
    function _fmtSource(order: any): string {
      var srcLabel = (order.source && order.source.label) || (order.source && order.source.key) || 'Ręczne';
      var srcColor = (order.source && order.source.color) || '';
      var acc = order.allegroAccountName || order._account_name || order.accountName || '';
      var inner = escapeHtml(srcLabel);
      if (acc) inner += ' <span style="opacity:.75;font-size:10px">· ' + escapeHtml(acc) + '</span>';
      return '<span class="status-badge ' + srcColor + '" style="white-space:nowrap">' + inner + '</span>';
    }
    function _fmtDateTime(order: any): string {
      var raw = order._sortDate || order.date || order.bought_at || order.dateFormatted || '';
      if (!raw) return escapeHtml(String(order.dateFormatted || ''));
      try {
        var d = new Date(raw);
        if (isNaN(d.getTime())) return escapeHtml(String(order.dateFormatted || ''));
        var pad = function(n: number) { return String(n).padStart(2, '0'); };
        return pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear() +
          ' <span style="opacity:.7">' + pad(d.getHours()) + ':' + pad(d.getMinutes()) + '</span>';
      } catch (_) {
        return escapeHtml(String(order.dateFormatted || ''));
      }
    }

    var html = '';
    var i18nMod = window.__sbI18n;
    paginated.forEach(function(order) {
      var isChecked = (state as any).ordersSelectedIds.has(order.id) ? ' checked' : '';
      var statusObj = coerceStatusObj((order as any).status);
      // ETAP 3: `etykietaStatusu` oddaje pierwszeństwo nazwie nadanej przez firmę,
      // a tłumaczy dopiero nazwy domyślne — inaczej `pl.json` nadpisywałby przemianowanie.
      var _etykieta = (window as any).__sbExports && (window as any).__sbExports.etykietaStatusu;
      var stLabel = _etykieta
        ? _etykieta(statusObj.key, statusObj.label)
        : (i18nMod && i18nMod.tStatus ? i18nMod.tStatus('order', statusObj.key, statusObj.label) : statusObj.label);
      var groupContextHtml = orderSearchGroupContextHtml(order);
      if (groupContextHtml) {
        html += '<tr class="orders-search-result-context-row"><td colspan="6">' +
          groupContextHtml + '</td></tr>';
      }
      html += '<tr data-order-id="' + escapeAttr(order.id) + '" class="order-row" style="cursor:pointer">' +
        '<td class="orders-table-check-col" style="' + checkColStyle + '" onclick="event.stopPropagation()"><input type="checkbox" class="order-row-check" data-order-id="' + escapeAttr(order.id) + '"' + isChecked + '></td>' +
        '<td style="max-width:340px;overflow:hidden;text-overflow:ellipsis">' + _fmtItems(order) + _noteFlagsHtml(order) + '</td>' +
        '<td style="white-space:nowrap"><span class="status-badge ' + statusObj.color + '" data-status-cat="order" data-status-key="' + escapeAttr(statusObj.key || '') + '">' + escapeHtml(stLabel) + '</span></td>' +
        '<td>' + _fmtSource(order) + '</td>' +
        '<td style="white-space:nowrap;font-weight:600;font-variant-numeric:tabular-nums">' + SellBaseData.formatCurrency(order.amount, (order as any).currency) + '</td>' +
        '<td style="white-space:nowrap;font-size:11px;font-variant-numeric:tabular-nums">' + _fmtDateTime(order) + '</td>' +
      '</tr>';
    });
    container.innerHTML = html || '<tr><td colspan="6" style="text-align:center;padding:24px;color:var(--color-text-muted)">Brak zamówień spełniających kryteria.</td></tr>';
    lucide.createIcons();

    // 2026-05-25: bind row clicks — klik wiersza otwiera drawer (jak w widoku kart).
    // Wywalone: view/edit/delete buttony — drawer zawiera wszystkie te akcje.
    container.querySelectorAll('.order-row').forEach(function(row) {
      row.addEventListener('click', function(this: any, e: any) {
        // Skip click on checkbox cell (event.stopPropagation w td)
        if ((e.target as HTMLElement).closest('.orders-table-check-col')) return;
        showOrderDetail(this.dataset.orderId);
      });
    });

    // Bind row checkboxes
    container.querySelectorAll('.order-row-check').forEach(function(cb) {
      cb.addEventListener('change', function(this: any) {
        var oid = this.dataset.orderId;
        if (this.checked) (state as any).ordersSelectedIds.add(oid);
        else (state as any).ordersSelectedIds.delete(oid);
        _updateBulkBar();
      });
    });

    // Check-all
    var checkAll = document.getElementById('orders-table-check-all');
    if (checkAll) {
      checkAll.addEventListener('change', function(this: any) {
        var cbs = container!.querySelectorAll('.order-row-check');
        cbs.forEach(function(cb) {
          (cb as any).checked = (checkAll! as any).checked;
          var oid = (cb as any).dataset.orderId;
          if ((checkAll! as any).checked) (state as any).ordersSelectedIds.add(oid);
          else (state as any).ordersSelectedIds.delete(oid);
        });
        _updateBulkBar();
      });
    }

    renderPagination('orders-pagination', page, totalPages, totalItems, 'orders');
    // Lazy background refresh dla widocznych PS orderów z pustym adresem (view table)
    setTimeout(function() { _lazyRefreshVisiblePSOrders(paginated); }, 800);
}

export function orderStatusOptionsHtml(selectedName: string) {
    var sorted = SellBaseData.orderStatuses.slice().sort(function(a: OrderStatusDef, b: OrderStatusDef) { return (a.sort_order || 0) - (b.sort_order || 0); });
    var html = '';
    sorted.forEach(function(s: OrderStatusDef) {
      var sel = (s.name === selectedName) ? ' selected' : '';
      html += '<option value="' + escapeAttr(s.name) + '"' + sel + '>' + escapeHtml(s.name) + '</option>';
    });
    return html;
}

// ── EXPOSE ──
(window as any).SellBaseApp = (window as any).SellBaseApp || {};
(window as any).renderOrdersTable = renderOrdersTable; (window as any).SellBaseApp.renderOrdersTable = renderOrdersTable;
(window as any).orderStatusOptionsHtml = orderStatusOptionsHtml; (window as any).SellBaseApp.orderStatusOptionsHtml = orderStatusOptionsHtml;
